import React, { useState } from 'react';
import { useProduct } from '../Context/ProductContext'; // تأكد من المسار الصحيح
import { useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiMapPin, FiPhone, FiUser, FiCreditCard, FiTrash2, FiPlus, FiMinus } from 'react-icons/fi';
import toast, { Toaster } from 'react-hot-toast';
import styles from './Checkout.module.css';

export default function Checkout() {
  const { cart, removeFromCart, updateCartItemQuantity, clearCart, getCartTotal, getCartItemsCount } = useProduct();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
    paymentMethod: 'cash',
  });

  const [orderPlaced, setOrderPlaced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // حساب الأسعار
  const subtotal = parseFloat(getCartTotal()) || 0;
  const delivery = subtotal > 0 ? 30 : 0;
  const tax = Math.round(subtotal * 0.1);
  const total = subtotal + delivery + tax;

  // تحديث كمية المنتج
  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      toast.success('Item removed from cart');
    } else {
      updateCartItemQuantity(productId, newQuantity);
    }
  };

  // معالجة تغيير البيانات
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ 
      ...formData, 
      [name]: value
    });
  };

  // التحقق من البيانات
  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your delivery address');
      return false;
    }
    if (!/^\d{10,}$/.test(formData.phone.replace(/[^\d]/g, ''))) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  // تقديم الطلب
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || cart.length === 0) {
      if (cart.length === 0) {
        toast.error('Your cart is empty');
      }
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading('Processing your order...');

    try {
      // هنا يمكنك إرسال الطلب للـ API
      const orderData = {
        customer: formData,
        items: cart,
        subtotal: subtotal,
        tax: tax,
        deliveryFee: delivery,
        total: total,
        paymentMethod: formData.paymentMethod,
        orderDate: new Date().toISOString()
      };
      
      console.log('Order Data:', orderData);

      // محاكاة delay API
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.dismiss(loadingToast);
      toast.success('Order placed successfully! 🎉', {
        duration: 4000,
        icon: '✅',
      });

      setOrderPlaced(true);

      setTimeout(() => {
        setOrderPlaced(false);
        clearCart();
        setFormData({ name: '', phone: '', address: '', notes: '', paymentMethod: 'cash' });
        // يمكنك إعادة التوجيه لصفحة التأكيد
        // navigate('/order-confirmation');
      }, 3000);

    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to place order. Please try again.');
      console.error('Order error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // الحصول على تسمية طريقة الدفع
  const getPaymentMethodLabel = () => {
    switch(formData.paymentMethod) {
      case 'cash': return '💵 Cash on Delivery';
      case 'card': return '🏦 Credit Card';
      case 'wallet': return '📱 Digital Wallet';
      case 'transfer': return '🏪 Bank Transfer';
      default: return 'Cash on Delivery';
    }
  };

  if (cart.length === 0 && !orderPlaced) {
    return (
      <div className={styles.emptyCart}>
        <FiShoppingCart size={80} className={styles.emptyIcon} />
        <h2>Your cart is empty</h2>
        <p>Add items to your cart before proceeding to checkout</p>
        <button 
          onClick={() => navigate('/all-products')}
          className={styles.continueShoppingBtn}
        >
          Continue Shopping
        </button>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className={styles.checkoutContainer}>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1f2937',
            color: '#fff',
            borderRadius: '0.5rem',
          },
          success: {
            style: {
              background: '#10b981',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444',
            },
          },
          loading: {
            style: {
              background: '#f59e0b',
            },
          },
        }}
      />

      <div className={styles.maxWidth}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FiShoppingCart className={styles.titleIcon} size={36} />
            Checkout
          </h1>
          <p className={styles.subtitle}>Complete your order and enjoy a delicious meal</p>
        </div>

        {/* رسالة النجاح */}
        {orderPlaced && (
          <div className={styles.successMessage}>
            <p>✅ Order placed successfully! Delivery coming soon</p>
          </div>
        )}

        <div className={styles.gridContainer}>
          {/* القسم الأيسر - سلة التسوق والبيانات */}
          <div className={styles.leftSection}>
            {/* سلة التسوق */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Your Cart ({getCartItemsCount()} items)</h2>
              
              {cart.length === 0 ? (
                <p className={styles.emptyMessage}>Cart is empty!</p>
              ) : (
                <div className={styles.cartItems}>
                  {cart.map(item => (
                    <div key={item.id} className={styles.cartItem}>
                      <div className={styles.itemInfo}>
                        {item.image ? (
                          <img src={item.image} alt={item.name} className={styles.itemImage} />
                        ) : (
                          <span className={styles.itemImagePlaceholder}>🍕</span>
                        )}
                        <div className={styles.itemDetails}>
                          <p className={styles.itemName}>{item.name}</p>
                          <p className={styles.itemPrice}>${item.price.toFixed(2)}</p>
                          {item.size && (
                            <p className={styles.itemMeta}>
                              <span className={styles.metaLabel}>Size:</span> {item.size}
                            </p>
                          )}
                          {item.spiceLevel && (
                            <p className={styles.itemMeta}>
                              <span className={styles.metaLabel}>Spice:</span> {item.spiceLevel}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className={styles.quantityControl}>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                          className={styles.quantityBtn}
                          title="Decrease quantity"
                        >
                          <FiMinus size={18} />
                        </button>
                        <span className={styles.quantity}>{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          className={`${styles.quantityBtn} ${styles.plusBtn}`}
                          title="Increase quantity"
                        >
                          <FiPlus size={18} />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className={`${styles.quantityBtn} ${styles.deleteBtn}`}
                          title="Remove item"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>

                      <div className={styles.itemTotal}>
                        <p className={styles.totalPrice}>
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* بيانات التوصيل */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Delivery Information</h2>
              
              <div className={styles.formGroup}>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>
                    <FiUser size={20} /> Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>
                    <FiPhone size={20} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>
                    <FiMapPin size={20} /> Delivery Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={styles.textarea}
                    placeholder="Enter your complete address"
                    rows="3"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Special Instructions</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className={styles.textarea}
                    placeholder="Any special requests?"
                    rows="2"
                  />
                </div>
              </div>
            </div>

            {/* طرق الدفع */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>
                <FiCreditCard size={24} /> Payment Method
              </h2>
              
              <div className={styles.paymentMethods}>
                {/* Cash on Delivery */}
                <label className={styles.paymentOption} style={{borderColor: formData.paymentMethod === 'cash' ? '#ff6b35' : '#e5e7eb'}}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cash"
                    checked={formData.paymentMethod === 'cash'}
                    onChange={handleInputChange}
                  />
                  <div className={styles.paymentEmoji}>💵</div>
                  <div className={styles.paymentContent}>
                    <p className={styles.paymentTitle}>Cash on Delivery</p>
                    <p className={styles.paymentDesc}>Pay the driver when your order arrives</p>
                  </div>
                </label>

                {/* Credit Card */}
                <label className={styles.paymentOption} style={{borderColor: formData.paymentMethod === 'card' ? '#3b82f6' : '#e5e7eb'}}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={formData.paymentMethod === 'card'}
                    onChange={handleInputChange}
                  />
                  <div className={styles.paymentEmoji}>🏦</div>
                  <div className={styles.paymentContent}>
                    <p className={styles.paymentTitle}>Credit Card</p>
                    <p className={styles.paymentDesc}>VISA, Mastercard, AmEx</p>
                  </div>
                </label>

                {/* Digital Wallet */}
                <label className={styles.paymentOption} style={{borderColor: formData.paymentMethod === 'wallet' ? '#a855f7' : '#e5e7eb'}}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="wallet"
                    checked={formData.paymentMethod === 'wallet'}
                    onChange={handleInputChange}
                  />
                  <div className={styles.paymentEmoji}>📱</div>
                  <div className={styles.paymentContent}>
                    <p className={styles.paymentTitle}>Digital Wallet</p>
                    <p className={styles.paymentDesc}>Apple Pay, Google Pay, Stripe</p>
                  </div>
                </label>

                {/* Bank Transfer */}
                <label className={styles.paymentOption} style={{borderColor: formData.paymentMethod === 'transfer' ? '#22c55e' : '#e5e7eb'}}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="transfer"
                    checked={formData.paymentMethod === 'transfer'}
                    onChange={handleInputChange}
                  />
                  <div className={styles.paymentEmoji}>🏪</div>
                  <div className={styles.paymentContent}>
                    <p className={styles.paymentTitle}>Bank Transfer</p>
                    <p className={styles.paymentDesc}>Direct transfer from your bank account</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* القسم الأيمن - ملخص الطلب */}
          <div className={styles.rightSection}>
            <div className={styles.summaryCard}>
              <h2 className={styles.summaryTitle}>
                <FiCreditCard size={28} /> Summary
              </h2>

              <div className={styles.summaryDetails}>
                <div className={styles.summaryRow}>
                  <span>Subtotal ({getCartItemsCount()} items)</span>
                  <span className={styles.summaryValue}>${subtotal.toFixed(2)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Tax (10%)</span>
                  <span className={styles.summaryValue}>${tax}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Delivery Fee</span>
                  <span className={styles.summaryValue}>${delivery}</span>
                </div>
              </div>

              <div className={styles.totalRow}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>

              <div className={styles.paymentInfo}>
                <p className={styles.paymentInfoTitle}>💳 Selected Payment:</p>
                <p className={styles.paymentInfoValue}>{getPaymentMethodLabel()}</p>
              </div>

              <button
                onClick={handleSubmitOrder}
                disabled={cart.length === 0 || !formData.name || !formData.phone || !formData.address || isLoading}
                className={styles.confirmBtn}
              >
                {isLoading ? '⏳ Processing...' : '✓ Confirm Order'}
              </button>

              <p className={styles.securityText}>Your order will be processed securely</p>

              <button
                onClick={() => navigate(-1)}
                className={styles.backBtn}
                disabled={isLoading}
              >
                ← Back to Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}